<template>
    <div class="kb-seasons-bar">
        <div class="kb-seasons-bar__strip">
            <div
                v-for="(month, i) in months"
                :key="i"
                class="kb-seasons-bar__month"
                :class="{'kb-seasons-bar__month--current': month.isCurrent}"
            >
                <div class="kb-seasons-bar__month-label">
                    {{ month.label }}
                </div>
                <div
                    v-if="month.showYear"
                    class="kb-seasons-bar__year-label"
                >
                    {{ month.year }}
                </div>
            </div>

            <div
                v-for="ann in announcementBands"
                :key="ann.key"
                class="kb-seasons-bar__announcement"
                :class="{
                    'kb-seasons-bar__announcement--cut-start': ann.cutAtStart,
                    'kb-seasons-bar__announcement--cut-end': ann.cutAtEnd,
                }"
                :style="{left: ann.left + '%', width: ann.width + '%'}"
                :title="ann.title"
            >
                <span class="kb-seasons-bar__announcement-label">{{ ann.label }}</span>
            </div>

            <component
                :is="block.clickable ? 'button' : 'div'"
                v-for="block in blocks"
                :key="block.key"
                class="kb-seasons-bar__block"
                :class="{
                    'kb-seasons-bar__block--empty': block.hatched,
                    'kb-seasons-bar__block--active': block.isActive,
                    'kb-seasons-bar__block--clickable': block.clickable,
                    'kb-seasons-bar__block--cut-start': block.cutAtStart,
                    'kb-seasons-bar__block--cut-end': block.cutAtEnd,
                }"
                :style="{
                    left: block.left + '%',
                    width: block.width + '%',
                    'background-color': block.background,
                    color: block.textColor,
                }"
                :title="block.title"
                @click="block.clickable ? $emit('add-override', block.seasonId) : null"
            >
                <span v-if="block.hatched" class="kb-seasons-bar__block-plus">+</span>
                <span class="kb-seasons-bar__block-label">{{ block.label }}</span>
            </component>

            <div
                class="kb-seasons-bar__today"
                :style="{left: todayLeft + '%'}"
                :title="todayTitle"
            >
                <span class="kb-seasons-bar__today-label">{{ $t('knowledgebase.today') }}</span>
            </div>
        </div>

        <p
            v-if="unplannedSeasons.length > 0"
            class="kb-seasons-bar__unplanned"
        >
            {{ $tc(
                'knowledgebase.unplannedSeasons',
                unplannedSeasons.length,
                {count: unplannedSeasons.length, names: unplannedNames}
            ) }}
        </p>
    </div>
</template>

<script>
const MONTHS_BEFORE = 3;
const MONTHS_AFTER = 8;
const TOTAL_MONTHS = MONTHS_BEFORE + 1 + MONTHS_AFTER;

const MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function parseDate(s) {
    // 'YYYY-MM-DD' from API; new Date(s) is timezone-flaky, so build it explicitly.
    if(!s) return null;
    const parts = s.split('-');
    if(parts.length !== 3) return null;
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function endOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

export default {
    name: 'kb-seasons-bar',

    props: {
        seasons: {type: Array, required: true},
        // Optional: variant data for an article. When provided, blocks for
        // seasons with a matching variant render solid; others render hatched
        // (falling through to the default body). When omitted, all blocks
        // render solid — the overview-page presentation.
        variants: {type: Array, default: null},
    },

    computed: {
        today() {
            return new Date();
        },
        windowStart() {
            return addMonths(startOfMonth(this.today), -MONTHS_BEFORE);
        },
        windowEnd() {
            // Exclusive — first day of the month after the last visible month.
            return addMonths(this.windowStart, TOTAL_MONTHS);
        },
        windowDays() {
            return (this.windowEnd - this.windowStart) / 86400000;
        },
        months() {
            const out = [];
            for(let i = 0; i < TOTAL_MONTHS; i++) {
                const m = addMonths(this.windowStart, i);
                const isCurrent = m.getFullYear() === this.today.getFullYear() &&
                    m.getMonth() === this.today.getMonth();
                out.push({
                    label: MONTH_LABELS[m.getMonth()],
                    year: m.getFullYear(),
                    showYear: i === 0 || m.getMonth() === 0,
                    isCurrent,
                });
            }
            return out;
        },
        announcementBands() {
            if(!this.variants) return [];
            const winStart = this.windowStart.getTime();
            const winEnd = this.windowEnd.getTime();
            const out = [];
            for(const v of this.variants) {
                if(v.valid_from == null || v.will_be_deleted) continue;
                const start = parseDate(v.valid_from);
                const end = parseDate(v.valid_to);
                if(!start || !end) continue;
                const startTs = start.getTime();
                const endTs = endOfDay(end).getTime();
                if(endTs < winStart || startTs > winEnd) continue;
                const clippedStart = Math.max(startTs, winStart);
                const clippedEnd = Math.min(endTs, winEnd);
                const left = (clippedStart - winStart) / 86400000 / this.windowDays * 100;
                const width = (clippedEnd - clippedStart) / 86400000 / this.windowDays * 100;
                out.push({
                    key: `ann-${v.id}`,
                    label: v.label || '',
                    title: v.label || `${v.valid_from} – ${v.valid_to}`,
                    left,
                    width,
                    cutAtStart: startTs < winStart,
                    cutAtEnd: endTs > winEnd,
                });
            }
            return out;
        },
        variantSeasonIds() {
            if(!this.variants) return null;
            const set = new Set();
            for(const v of this.variants) {
                if(v.season != null && v.valid_from == null) set.add(v.season);
            }
            return set;
        },
        blocks() {
            const out = [];
            const winStart = this.windowStart.getTime();
            const winEnd = this.windowEnd.getTime();
            const todayTs = endOfDay(this.today).getTime();

            for(const season of this.seasons) {
                const hasVariant = this.variantSeasonIds
                    ? this.variantSeasonIds.has(season.id)
                    : true;
                const periods = season.time_periods || [];
                for(let i = 0; i < periods.length; i++) {
                    const p = periods[i];
                    const start = parseDate(p.date_start);
                    const end = parseDate(p.date_end);
                    if(!start || !end) continue;
                    const startTs = start.getTime();
                    const endTs = endOfDay(end).getTime();
                    if(endTs < winStart || startTs > winEnd) continue;

                    const clippedStart = Math.max(startTs, winStart);
                    const clippedEnd = Math.min(endTs, winEnd);
                    const left = (clippedStart - winStart) / 86400000 / this.windowDays * 100;
                    const width = (clippedEnd - clippedStart) / 86400000 / this.windowDays * 100;
                    const isActive = todayTs >= startTs && todayTs <= endTs;
                    const cutAtStart = startTs < winStart;
                    const cutAtEnd = endTs > winEnd;

                    // Empty (no override) blocks render uniformly in grey so the
                    // bar isn't a rainbow of colors competing for attention —
                    // only the *customized* seasons keep their identifying color.
                    const empty = !hasVariant;
                    const clickable = empty && this.variants != null;
                    out.push({
                        key: `${season.id}-${i}`,
                        seasonId: season.id,
                        label: season.name,
                        title: empty
                            ? this.$t('knowledgebase.seasonsBarAddTitle', {season: season.name})
                            : this.$t('knowledgebase.seasonsBarSeasonTitle', {season: season.name}),
                        left,
                        width,
                        background: empty ? '#e8eaed' : (season.color || '#cccccc'),
                        textColor: empty ? '#7a7a7a' : this._textColorFor(season.color),
                        hatched: empty,
                        isActive,
                        clickable,
                        cutAtStart,
                        cutAtEnd,
                    });
                }
            }
            return out;
        },
        todayLeft() {
            const ts = this.today.getTime();
            const winStart = this.windowStart.getTime();
            return (ts - winStart) / 86400000 / this.windowDays * 100;
        },
        todayTitle() {
            return this.today.toLocaleDateString();
        },
        unplannedSeasons() {
            const todayTs = startOfMonth(this.today).getTime();
            return this.seasons.filter(s => {
                const periods = s.time_periods || [];
                if(periods.length === 0) return true;
                // Unplanned if every period ends before today.
                return periods.every(p => {
                    const end = parseDate(p.date_end);
                    return end && end.getTime() < todayTs;
                });
            });
        },
        unplannedNames() {
            return this.unplannedSeasons.map(s => s.name).join(', ');
        },
    },

    methods: {
        _textColorFor(hex) {
            if(!hex) return '#333';
            // Quick perceived-luminance check; dark text on light fills.
            const h = hex.replace('#', '');
            if(h.length !== 6) return '#333';
            const r = parseInt(h.slice(0, 2), 16);
            const g = parseInt(h.slice(2, 4), 16);
            const b = parseInt(h.slice(4, 6), 16);
            const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            return lum > 0.6 ? '#333' : '#fff';
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-seasons-bar {
    margin: 8px 0 0;
}

.kb-seasons-bar__strip {
    position: relative;
    display: flex;
    height: 88px;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    background: #fafafa;
    overflow: hidden;
}

.kb-seasons-bar__month {
    flex: 1;
    min-width: 0;
    border-right: 1px dashed #eaeaea;
    position: relative;
    padding-top: 8px;

    &:last-child {
        border-right: 0;
    }

    &--current {
        background: rgba(0, 0, 0, 0.025);
    }
}

.kb-seasons-bar__month-label {
    text-align: center;
    font-size: 0.82rem;
    color: #999;
    line-height: 1;
}

.kb-seasons-bar__year-label {
    position: absolute;
    top: 3px;
    left: 5px;
    font-size: 0.68rem;
    color: #bbb;
}

.kb-seasons-bar__block {
    position: absolute;
    top: 46px;
    height: 36px;
    border-radius: 4px;
    overflow: hidden;
    padding: 0 8px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.85rem;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
    appearance: none;
    border: 0;
    font-family: inherit;
    text-align: left;

    &--empty {
        /* Uniform grey — only customized seasons keep their identifying color. */
        border: 1px dashed #c5c8cc;
        box-shadow: none;
    }

    &--cut-start {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    &--cut-end {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    &--clickable {
        cursor: pointer;
        transition: background-color 0.12s, transform 0.12s;

        &:hover {
            background-color: #dde0e3 !important;
            transform: translateY(-1px);
        }
    }

}

.kb-seasons-bar__block-label {
    overflow: hidden;
    text-overflow: ellipsis;
}

.kb-seasons-bar__block-plus {
    font-size: 1rem;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
}

.kb-seasons-bar__announcement {
    position: absolute;
    top: 30px;
    height: 12px;
    background: #c44b8a;
    color: #fff;
    border-radius: 2px;
    padding: 0 4px;
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 12px;
    white-space: nowrap;
    overflow: hidden;
    z-index: 2;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);

    &--cut-start {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    &--cut-end {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
}

.kb-seasons-bar__announcement-label {
    text-overflow: ellipsis;
    overflow: hidden;
}

.kb-seasons-bar__today {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0;
    border-left: 2px solid #009bba;
    z-index: 3;
}

.kb-seasons-bar__today-label {
    position: absolute;
    top: 2px;
    left: 3px;
    font-size: 0.6rem;
    font-weight: 700;
    color: #009bba;
    background: rgba(255, 255, 255, 0.85);
    padding: 1px 4px;
    border-radius: 2px;
    line-height: 1;
}

.kb-seasons-bar__unplanned {
    margin-top: 6px;
    font-size: 0.8rem;
    color: #c95;
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-seasons-bar.vue